/**
 * Plan Storage
 *
 * Domain-shaped Adapter for the `floorplans` private bucket. Owns
 * everything callers used to need to know about how a Plan's bytes are
 * laid out in Storage:
 *
 *   `{plan_id}/source.{ext}`           -- the original upload (image or PDF)
 *   `{plan_id}/pages/{page_id}.{ext}`  -- per-Floor raster
 *
 * The interface is shaped around what Floor Intake actually does — put
 * an image Floor's raster, put a PDF source, put one rasterized page,
 * sign a path for display, clean up — not around bucket primitives.
 * That keeps path conventions in one place: the RLS policies still
 * enforce them server-side, but the frontend only needs to hold a
 * reference to whatever path `putXxx` returned.
 *
 * The exported `planStorage` is the production Adapter. Tests can swap
 * its operations through the `setAssetUrlMinter` seam in
 * `asset-url-cache.ts` (for read paths) and through `vi.mock` of this
 * module (for write paths).
 */

import { supabase } from './supabase';

const FLOORPLAN_BUCKET = 'floorplans';

const SIGNED_URL_TTL_SECONDS = 60 * 60;

const allowedMimeToExt: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
};

/**
 * Pick a safe extension for an uploaded image based on its MIME type.
 * Falls back to the original filename's extension only as a last resort.
 */
const imageExtensionFor = (file: File): string => {
  const fromMime = allowedMimeToExt[file.type];
  if (fromMime) return fromMime;
  const dot = file.name.lastIndexOf('.');
  if (dot > 0) {
    const ext = file.name.slice(dot + 1).toLowerCase();
    if (ext.length <= 5) return ext;
  }
  return 'bin';
};

const sourcePath = (planId: string, ext: string): string =>
  `${planId}/source.${ext}`;

const pageRasterPath = (planId: string, pageId: string, ext: string): string =>
  `${planId}/pages/${pageId}.${ext}`;

const upload = async (
  path: string,
  file: File | Blob,
  contentType: string,
): Promise<void> => {
  // `upsert: true` so retries after a partial failure don't error out;
  // RLS verifies Plan ownership server-side.
  const { error } = await supabase.storage
    .from(FLOORPLAN_BUCKET)
    .upload(path, file, { contentType, upsert: true });
  if (error) throw error;
};

export interface PlanStorage {
  /** Upload an image-based Floor's raster. Returns the storage path the
   *  caller should write to `image_url` / `source_url` on the row. */
  putImageFloor: (planId: string, pageId: string, file: File) => Promise<string>;

  /** Upload the source PDF for a Plan. The same path is shared by every
   *  Floor produced from that PDF, so all rasters can reference one
   *  source upload via `source_url`. */
  putPdfSource: (planId: string, file: File) => Promise<string>;

  /** Upload one rasterized page of a PDF Floor. */
  putPdfRaster: (
    planId: string,
    pageId: string,
    raster: Blob,
  ) => Promise<string>;

  /** Remove a single object — used to roll back a partial intake. */
  remove: (path: string) => Promise<void>;

  /** Remove every object stored under a Plan. Called before deleting
   *  the row so RLS still permits the delete (after the row is gone the
   *  policy can't resolve owner). */
  removePlanTree: (planId: string) => Promise<void>;

  /** Mint a short-lived URL the editor can hand to an `<img>`. */
  signedUrl: (path: string) => Promise<string>;
}

export const planStorage: PlanStorage = {
  putImageFloor: async (planId, pageId, file) => {
    const path = pageRasterPath(planId, pageId, imageExtensionFor(file));
    await upload(path, file, file.type);
    return path;
  },

  putPdfSource: async (planId, file) => {
    const path = sourcePath(planId, 'pdf');
    await upload(path, file, file.type);
    return path;
  },

  putPdfRaster: async (planId, pageId, raster) => {
    const path = pageRasterPath(planId, pageId, 'png');
    await upload(path, raster, 'image/png');
    return path;
  },

  remove: async (path) => {
    const { error } = await supabase.storage
      .from(FLOORPLAN_BUCKET)
      .remove([path]);
    if (error) throw error;
  },

  removePlanTree: async (planId) => {
    const collected: string[] = [];
    const { data: topLevel, error: listError } = await supabase.storage
      .from(FLOORPLAN_BUCKET)
      .list(planId, { limit: 1000 });
    if (listError) throw listError;

    for (const entry of topLevel ?? []) {
      if (entry.name === '.emptyFolderPlaceholder') continue;
      if (entry.id === null) {
        // Folder. Phase 1 only nests `pages/`.
        const folder = `${planId}/${entry.name}`;
        const { data: nested, error: nestedError } = await supabase.storage
          .from(FLOORPLAN_BUCKET)
          .list(folder, { limit: 1000 });
        if (nestedError) throw nestedError;
        for (const inner of nested ?? []) {
          if (inner.name === '.emptyFolderPlaceholder') continue;
          collected.push(`${folder}/${inner.name}`);
        }
      } else {
        collected.push(`${planId}/${entry.name}`);
      }
    }

    if (collected.length === 0) return;

    const { error: removeError } = await supabase.storage
      .from(FLOORPLAN_BUCKET)
      .remove(collected);
    if (removeError) throw removeError;
  },

  signedUrl: async (path) => {
    const { data, error } = await supabase.storage
      .from(FLOORPLAN_BUCKET)
      .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
    if (error) throw error;
    return data.signedUrl;
  },
};
